import React, { useEffect, useState, useMemo } from 'react'
import { Flex, Spin } from 'antd'

import { PageLayout } from '@/shared/ui/layout'
import { AccountSelectionCard } from './components/AccountSelectionCard'
import { TransferModeCard } from './components/TransferModeCard'
import { DataTransferCard } from './components/DataTransferCard'
import { TransferKey } from 'antd/es/transfer/interface'
import { Account, useInitialAccountPage } from '@/widgets/accountsGrid'
import { getSourceApi } from '@/shared/api/config'
import { useAuthStore } from '@/app/store/authStore'
import { useGlonassoftSourceQueries } from '../model/useGlonassoftSourceQueries'
import { useTransferDataForTransfer } from '../model/useTransferDataForTransfer'
import { useAgentsInfoQuery } from '../model/useAgentsInfoQuery'
import { useAgentsQuery } from '@/shared/api/useAgentsQuery'
import { useSystemUrl } from '../model/useSystemUrl'
import { useStartTransfer } from '../model/useStartTransfer'
import { useQueryClient } from '@tanstack/react-query'
import { SourceAccount } from '@/app/types/source'
import {
  DEFAULT_MESSAGE_PERIOD,
  TRANSFER_MODES,
  TransferMode,
} from '../model/transferTypes'
import { TASK_TYPES } from '@/entities/task/model/taskTypes'
import { SYSTEM_TYPE, useSystemStore } from '@/app/store/systemStore'
import { getSystemDisplayName } from '@/shared/lib/getSystemDisplayName'
import { useGlonassoftStore } from '@/app/store/glonassoftStore'
import { SourceAccountCard } from './components/SourceAccountCard'
import { GsGridTransferEntites } from './components/GsGridTransferEntities'
import { TransferButtonSection } from './components/TransferButtonSetion/TransferButtonSection'
import { SectionCard } from '@/shared/ui/layout/styled'
import { MessagePeriodSelect } from './components/DataTransferCard/ui/MessagePeriodSelect'
import { buildReceiverHostUrl } from '@/shared/lib/buildReceiverHostUrl'

export const GlonassoftTransferPage: React.FC = () => {
  const { user, userPassword, setTokenAccount, tokenAccount, currentServer } =
    useAuthStore()
  const { credentials: glonassoftCredentialsData, server: gsServer } =
    useGlonassoftStore()
  const { selectedSystem } = useSystemStore()
  const [selectedSubpartner, setSelectedSubpartner] = useState('')
  const [userSelectedSubpartner, setUserSelectedSubpartner] = useState(false)
  const [isMyAccountSelected, setIsMyAccountSelected] = useState(false)
  const { systemUrl } = useSystemUrl()
  const [selectedAccount, setSelectedAccount] = useState('')
  const [transferMode, setTransferMode] = useState<TransferMode>(
    TRANSFER_MODES.ENTITIES
  )
  const [objectsTargetKeys, setObjectsTargetKeys] = useState<TransferKey[]>([])
  const [usersTargetKeys, setUsersTargetKeys] = useState<TransferKey[]>([])
  const [loadMessages, setLoadMessages] = useState(false)
  const [loadDriversCodes, setLoadDriversCodes] = useState(false)

  const [retranslationsData, setRetranslationsData] = useState<any[]>([])
  const [retranslationsTargetKeys, setRetranslationsTargetKeys] = useState<
    TransferKey[]
  >([])

  const [accountsTargetKeys, setAccountsTargetKeys] = useState<TransferKey[]>(
    []
  )

  const [geozonesTargetKeys, setGeozonesTargetKeys] = useState<TransferKey[]>(
    []
  )
  const [driversTargetKeys, setDriversTargetKeys] = useState<TransferKey[]>([])

  const [loadMessagesFull, setLoadMessagesFull] = useState(false)
  const [messagePeriod, setMessagePeriod] = useState(DEFAULT_MESSAGE_PERIOD)
  const [messagePeriodFull, setMessagePeriodFull] = useState(
    DEFAULT_MESSAGE_PERIOD
  )

  const [targetAgentAccounts, setTargetAgentAccounts] = useState<Account[]>([])
  const [sourceAgentAccounts, setSourceAgentAccounts] = useState<Account[]>([])

  const queryClient = useQueryClient()
  const sourceApiUrl = getSourceApi(selectedSystem)

  const glonassoftCredentials = useMemo(() => {
    if (glonassoftCredentialsData) {
      return glonassoftCredentialsData
    }
    return null
  }, [glonassoftCredentialsData])

  const glonassoftAuthParam = useMemo(() => {
    if (glonassoftCredentials) {
      return JSON.stringify(glonassoftCredentials)
    }
    return null
  }, [glonassoftCredentials])

  const {
    vehicles: glonassoftVehicles,
    drivers: glonassoftDrivers,
    users: glonassoftUsers,
    geozones: glonassoftGeozones,
    retranslations: glonassoftRetranslations,
    isLoading: isLoadingSourceData,
  } = useGlonassoftSourceQueries(
    selectedSubpartner,
    glonassoftCredentials,
    sourceApiUrl,
    systemUrl
  )

  const targetAgentsInfoQuery = useAgentsInfoQuery({
    username: user?.username,
    userPassword,
    currentServer,
  })

  useEffect(() => {
    if (targetAgentsInfoQuery.data)
      setTargetAgentAccounts(targetAgentsInfoQuery.data)
  }, [targetAgentsInfoQuery.data])

  const sourceAgentsInfoQuery = useAgentsInfoQuery({
    username: glonassoftCredentials?.login,
    userPassword: glonassoftCredentials?.password,
    currentServer: gsServer || undefined,
  })

  useEffect(() => {
    if (sourceAgentsInfoQuery.data) {
      setSourceAgentAccounts(sourceAgentsInfoQuery.data)
    }
  }, [sourceAgentsInfoQuery.data])

  const initialAccountPage = useInitialAccountPage(
    sourceAgentAccounts,
    selectedSubpartner ? [selectedSubpartner] : []
  )

  const activeCredentials = glonassoftCredentials

  const agentsQuery = useAgentsQuery({
    systemType: (selectedSystem ?? SYSTEM_TYPE.WIALON) as SYSTEM_TYPE,
    credentials: activeCredentials,
    sourceApiUrl,
    systemUrl,
  })

  useEffect(() => {
    const data = agentsQuery.data?.data
    if (!data) return
    setAccountsData(data)
    if (data.length > 0) {
      const mainId = data[0].id
      setSubPartners(
        data.filter((account: SourceAccount) => account.id !== mainId)
      )
      setTokenAccount(data[0].name || 'ГЛОНАССSoft User')
    }
  }, [agentsQuery.data])

  const handleGeozonesChange = (newTargetKeys: TransferKey[]) => {
    setGeozonesTargetKeys(newTargetKeys)
  }

  const handleDriversChange = (newTargetKeys: TransferKey[]) => {
    setDriversTargetKeys(newTargetKeys)
  }

  const handleSelectAllGeozones = () => {
    const dataToUse = geozonesDataForTransfer
    if (dataToUse.length > 0) {
      setGeozonesTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllGeozones = () => {
    setGeozonesTargetKeys([])
  }

  const handleSelectAllDrivers = () => {
    const dataToUse = driversDataForTransfer
    if (dataToUse.length > 0) {
      setDriversTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllDrivers = () => {
    setDriversTargetKeys([])
  }

  const handleAccountsChange = (newTargetKeys: TransferKey[]) => {
    setAccountsTargetKeys(newTargetKeys)
  }

  const handleSelectAllAccounts = () => {
    const dataToUse = accountsDataForTransfer
    if (dataToUse.length > 0) {
      setAccountsTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllAccounts = () => {
    setAccountsTargetKeys([])
  }

  const [vehiclesData, setVehiclesData] = useState<any[]>([])
  const [driversData, setDriversData] = useState<any[]>([])
  const [usersData, setUsersData] = useState<any[]>([])
  const [geosData, setGeosData] = useState<any[]>([])
  const [accountsData, setAccountsData] = useState<any[]>([])
  const [subPartners, setSubPartners] = useState<any[]>([])

  const {
    objectsDataForTransfer,
    usersDataForTransfer,
    geozonesDataForTransfer,
    driversDataForTransfer,
    accountsDataForTransfer,
    retranslationsDataForTransfer,
  } = useTransferDataForTransfer({
    vehiclesData,
    driversData,
    usersData,
    geosData,
    accountsData,
    retranslationsData,
    selectedSubpartner,
    userSelectedSubpartner,
    transferMode,
    shouldFilterByParentId: true,
  })

  useEffect(() => {
    setObjectsTargetKeys([])
    setUsersTargetKeys([])
    setGeozonesTargetKeys([])
    setDriversTargetKeys([])
    setRetranslationsTargetKeys([])
    // Каждый вход в FULL — чистый старт: только аккаунт из SourceAccountCard,
    // остальное накопленное в гриде (не видно после пролистывания 5000 страниц) сбрасываем.
    if (transferMode === TRANSFER_MODES.FULL) {
      setAccountsTargetKeys(selectedSubpartner ? [selectedSubpartner] : [])
    }
  }, [selectedSubpartner, transferMode])

  const handleObjectsChange = (newTargetKeys: TransferKey[]) => {
    setObjectsTargetKeys(newTargetKeys)
  }

  const handleUsersChange = (newTargetKeys: TransferKey[]) => {
    setUsersTargetKeys(newTargetKeys)
  }

  const handleSelectAllObjects = () => {
    const dataToUse = objectsDataForTransfer
    if (dataToUse.length > 0) {
      setObjectsTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllObjects = () => {
    setObjectsTargetKeys([])
  }

  const handleSelectAllUsers = () => {
    const dataToUse = usersDataForTransfer
    if (dataToUse.length > 0) {
      setUsersTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllUsers = () => {
    setUsersTargetKeys([])
  }

  const handleLoadMessagesChange = (checked: boolean) => {
    setLoadMessages(checked)
  }

  const handleMessagePeriodChange = (period: number) => {
    setMessagePeriod(period)
  }

  const handleLoadDriversCodes = (checked: boolean) => {
    setLoadDriversCodes(checked)
  }

  const handleLoadMessagesFullChange = (checked: boolean) => {
    setLoadMessagesFull(checked)
  }

  const handleMessagePeriodFullChange = (period: number) => {
    setMessagePeriodFull(period)
  }

  const handleRetranslationsChange = (newTargetKeys: TransferKey[]) => {
    setRetranslationsTargetKeys(newTargetKeys)
  }

  const handleSelectAllRetranslations = () => {
    const dataToUse = retranslationsDataForTransfer
    if (dataToUse.length > 0) {
      setRetranslationsTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllRetranslations = () => {
    setRetranslationsTargetKeys([])
  }

  useEffect(() => {
    if (glonassoftVehicles && !glonassoftVehicles.error)
      setVehiclesData(glonassoftVehicles.data)
    if (glonassoftDrivers && !glonassoftDrivers.error)
      setDriversData(glonassoftDrivers.data)
    if (glonassoftUsers && !glonassoftUsers.error)
      setUsersData(glonassoftUsers.data)
    if (glonassoftGeozones && !glonassoftGeozones.error)
      setGeosData(glonassoftGeozones.data)
    if (glonassoftRetranslations && !glonassoftRetranslations.error)
      setRetranslationsData(glonassoftRetranslations.data)
  }, [
    glonassoftVehicles,
    glonassoftDrivers,
    glonassoftUsers,
    glonassoftGeozones,
    glonassoftRetranslations,
  ])

  const buildSourceAuth = () => {
    let auth = glonassoftAuthParam
    if (glonassoftAuthParam && selectedSubpartner) {
      const parsed = JSON.parse(glonassoftAuthParam)
      auth = JSON.stringify({ ...parsed, agentId: selectedSubpartner })
    }
    return { auth }
  }

  const getUploadAccountName = (agentIds: string[]) => {
    if (transferMode === TRANSFER_MODES.FULL && agentIds.length > 0) {
      const selectedAccNames = agentIds.slice(0, 4).map((id) => {
        const agent = accountsData.find((acc) => acc.id === id)
        return agent?.name || ''
      })

      return selectedAccNames.length > 3
        ? `${selectedAccNames[0]}, ${selectedAccNames[1]}, ${selectedAccNames[2]}, ...`
        : selectedAccNames.join(', ')
    }
    return tokenAccount || getSystemDisplayName(selectedSystem)
  }

  const { handleStartTransfer, isTransferring } = useStartTransfer({
    transferMode,
    isMyAccountSelected,
    selectedAccount,
    targetAgentAccounts,
    selectedSubpartner,
    accountsData,
    subPartners,
    accountsTargetKeys,
    usersTargetKeys,
    objectsTargetKeys,
    driversTargetKeys,
    geozonesTargetKeys,
    retranslationsTargetKeys,
    loadMessages,
    messagePeriod,
    loadMessagesFull,
    messagePeriodFull,
    loadDriversCodes,
    systemUrl: buildReceiverHostUrl(systemUrl),
    buildSourceAuth,
    extraSubTaskCodes:
      transferMode === TRANSFER_MODES.FULL ? [...TASK_TYPES.GS_SPECIFIC] : [],
    getUploadAccountName,
    sourceId: (transferMode === TRANSFER_MODES.FULL
      ? accountsTargetKeys[0]
      : selectedSubpartner) as string,
  })

  const handleAccountSelect = (accountId: string) => {
    setSelectedAccount(accountId)
    setIsMyAccountSelected(false)
  }

  const handleMyAccountSelect = (checked: boolean) => {
    setIsMyAccountSelected(checked)
    if (checked) {
      setSelectedAccount('')
    }
  }

  const handleTransferModeChange = (mode: TransferMode) => {
    queryClient.cancelQueries({
      predicate: (query) => {
        const key = query.queryKey
        return (
          Array.isArray(key) &&
          key[0] === 'glonassoft-source' &&
          key[1] !== 'Agents'
        )
      },
    })
    setTransferMode(mode)
  }

  const handlePartnerSelect = (partnerId: string) => {
    setSelectedSubpartner(partnerId)
    setUserSelectedSubpartner(true)
  }

  const isAccountNotSelected = !selectedAccount && !isMyAccountSelected
  const isButtonDisabled = isAccountNotSelected || isTransferring

  return (
    <PageLayout
      title="Перенос данных между системами ГЛОНАССSoft"
      subtitle="Настройте параметры для переноса"
    >
      <Flex vertical gap={32}>
        <Spin spinning={isLoadingSourceData || isTransferring}>
          {transferMode === TRANSFER_MODES.ENTITIES ? (
            <SourceAccountCard
              accounts={sourceAgentAccounts}
              selectedAccount={selectedSubpartner}
              onAccountSelect={handlePartnerSelect}
              isLoading={sourceAgentsInfoQuery.isLoading}
              currentPage={initialAccountPage}
            />
          ) : (
            <GsGridTransferEntites
              isTransfering={isTransferring}
              accounts={sourceAgentAccounts}
              selected={accountsTargetKeys as string[]}
              onSelect={setAccountsTargetKeys}
              isLoading={sourceAgentsInfoQuery.isLoading}
              currentPage={initialAccountPage}
            />
          )}
        </Spin>

        <Spin spinning={isTransferring}>
          <AccountSelectionCard
            selectedAccount={selectedAccount}
            onAccountSelect={handleAccountSelect}
            isLoading={targetAgentsInfoQuery.isLoading}
            accounts={targetAgentAccounts}
            isMyAccountSelected={isMyAccountSelected}
            onMyAccountSelect={handleMyAccountSelect}
          />
        </Spin>

        <Spin spinning={isLoadingSourceData || isTransferring}>
          <TransferModeCard
            transferMode={transferMode}
            onTransferModeChange={handleTransferModeChange}
          />
        </Spin>

        <SectionCard>
          {transferMode === TRANSFER_MODES.ENTITIES ? (
            <DataTransferCard
              isLoading={isLoadingSourceData}
              transferMode={transferMode}
              // Для режима "Только сущности"
              objectsTargetKeys={objectsTargetKeys}
              usersTargetKeys={usersTargetKeys}
              geozonesTargetKeys={geozonesTargetKeys}
              driversTargetKeys={driversTargetKeys}
              loadMessages={loadMessages}
              loadDriversCodes={loadDriversCodes}
              messagePeriod={messagePeriod}
              retranslationsTargetKeys={retranslationsTargetKeys}
              // Для режима "Полный перенос"
              accountsTargetKeys={accountsTargetKeys}
              loadMessagesFull={loadMessagesFull}
              messagePeriodFull={messagePeriodFull}
              // Обработчики для режима "Только сущности"
              onObjectsChange={handleObjectsChange}
              onUsersChange={handleUsersChange}
              onGeozonesChange={handleGeozonesChange}
              onDriversChange={handleDriversChange}
              onSelectAllObjects={handleSelectAllObjects}
              onDeselectAllObjects={handleDeselectAllObjects}
              onSelectAllUsers={handleSelectAllUsers}
              onDeselectAllUsers={handleDeselectAllUsers}
              onSelectAllGeozones={handleSelectAllGeozones}
              onDeselectAllGeozones={handleDeselectAllGeozones}
              onSelectAllDrivers={handleSelectAllDrivers}
              onDeselectAllDrivers={handleDeselectAllDrivers}
              onLoadMessagesChange={handleLoadMessagesChange}
              onMessagePeriodChange={handleMessagePeriodChange}
              onLoadDriversCodesChange={handleLoadDriversCodes}
              onRetranslationsChange={handleRetranslationsChange}
              onSelectAllRetranslations={handleSelectAllRetranslations}
              onDeselectAllRetranslations={handleDeselectAllRetranslations}
              // Обработчики для режима "Полный перенос"
              onAccountsChange={handleAccountsChange}
              onSelectAllAccounts={handleSelectAllAccounts}
              onDeselectAllAccounts={handleDeselectAllAccounts}
              onLoadMessagesFullChange={handleLoadMessagesFullChange}
              onMessagePeriodFullChange={handleMessagePeriodFullChange}
              // Данные для передачи
              objectsData={objectsDataForTransfer}
              usersData={usersDataForTransfer}
              geozonesData={geozonesDataForTransfer}
              driversData={driversDataForTransfer}
              accountsData={accountsDataForTransfer}
              retranslationsData={retranslationsDataForTransfer}
            />
          ) : (
            <MessagePeriodSelect
              loadMessages={loadMessagesFull}
              messagePeriod={messagePeriodFull}
              onMessagePeriodChange={handleMessagePeriodFullChange}
              onLoadMessagesChange={handleLoadMessagesFullChange}
            />
          )}

          <TransferButtonSection
            transferMode={transferMode}
            disabled={isButtonDisabled || isTransferring}
            onClick={() => handleStartTransfer()}
            loading={isTransferring}
            objectsTargetKeys={objectsTargetKeys}
            usersTargetKeys={usersTargetKeys}
            geozonesTargetKeys={geozonesTargetKeys}
            driversTargetKeys={driversTargetKeys}
            retranslationsTargetKeys={retranslationsTargetKeys}
            accountsTargetKeys={accountsTargetKeys}
            text={
              isTransferring ? 'Выполняется перенос...' : 'Запустить перенос'
            }
          />
        </SectionCard>
      </Flex>
    </PageLayout>
  )
}
